import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';

interface GoogleSignInButtonProps {
  onSuccess?: () => void;
  onFailure?: (error: Error) => void;
  trialToken?: string | null;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, options: any) => void;
          prompt: () => void;
        };
      };
    };
    ENV?: {
      REACT_APP_API_URL?: string;
      REACT_APP_GOOGLE_CLIENT_ID?: string;
      REACT_APP_PUBLIC_POSTHOG_KEY?: string;
      REACT_APP_PUBLIC_POSTHOG_HOST?: string;
    };
  }
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onSuccess,
  onFailure,
  trialToken,
}) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const trialTokenRef = useRef<string | null>(null);
  trialTokenRef.current = trialToken ?? null;
  const { googleLogin } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const getGoogleClientId = (): string => {
      if (window.ENV?.REACT_APP_GOOGLE_CLIENT_ID) {
        return window.ENV.REACT_APP_GOOGLE_CLIENT_ID;
      }
      return process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
    };

    const clientId = getGoogleClientId();
    
    if (!clientId) {
      console.error('REACT_APP_GOOGLE_CLIENT_ID is not set');
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
    
    script.onload = () => {
      if (window.google && buttonRef.current) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse,
          locale: 'en',
        });
        
        window.google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          width: buttonRef.current.clientWidth,
        });
      }
    };
    
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);
  
  const handleCredentialResponse = async (response: any) => {
    setIsLoading(true);
    try {
      const result = await googleLogin(response.credential, trialTokenRef.current);

      if (result.success) {
        onSuccess && onSuccess();
      } else {
        onFailure && onFailure(new Error(result.error || 'Google Sign-In failed'));
      }
    } catch (error) {
      console.error('Google Sign-In error:', error);
      onFailure && onFailure(error instanceof Error ? error : new Error('Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <>
      <div ref={buttonRef} className={isLoading ? 'hidden' : 'w-full'}></div>
      {isLoading && (
        <div className="w-full flex items-center justify-center gap-3 h-10 border border-gray-300 rounded bg-white text-sm text-gray-600 font-medium">
          <svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Signing in…
        </div>
      )}
    </>
  );
};

export default GoogleSignInButton; 